import type { TTimelineItem } from "../../types";
import type { TrackingInterval } from "../contracts";
import { buildTrackingIntervals } from "../player/intervals";
import { isRecoveryPending } from "../recovery/contracts";
import { configurePixelBudget } from "./pixel-budget";
import { deviceComputeSlots } from "./scheduler";
import { trackingOptionsFor, type TrackingScopeInput } from "./result-cache";
import { TrackingSession } from "./session";
import type { SessionConfig } from "./session-runtime";

/**
 * Tracks videos that are not on screen, so switching to one shows finished
 * work instead of starting from nothing.
 *
 * It runs the same session a mounted player runs, on one worker, for one
 * video at a time. That is the point: interpolation, model recovery and
 * caching are the session's behaviour, not reimplemented here, so warmed work
 * is identical to work the player would have done and lands in the scope that
 * player later looks up.
 *
 * Paused whenever the asset on screen has work of its own. Background tracking
 * that slows down the video someone is watching is worse than none: compute is
 * the larger half of the pipeline, and these intervals are wanted eventually
 * rather than now.
 */

/**
 * One video to warm, described exactly as the player that will later show it
 * is described. Intervals and options are derived from these rather than
 * supplied, so the two cannot disagree about what the work is or where it
 * goes — a warmer that decided either for itself could write results the
 * player never looks for.
 */
export interface WarmTarget extends TrackingScopeInput {
  src: string;
  /** The same timeline data the player is given for this video. */
  items: TTimelineItem[];
}

export interface WarmerDependencies {
  createSession: (config: SessionConfig) => TrackingSession;
  /** Sizes the shared pixel ceiling when no player has mounted to do it. */
  computeSlots: () => number;
}

const browserDependencies: WarmerDependencies = {
  // One worker: this is work nobody is waiting on, and every slot reserves its
  // own share of cropped pixels from the ceiling the player also draws on.
  createSession: (config) => new TrackingSession(config, { workerCount: () => 1 }),
  computeSlots: deviceComputeSlots,
};

/** What warming this target would cover, so a rewrite that changes nothing is seen as such. */
const signature = (target: WarmTarget): string =>
  JSON.stringify(trackingOptionsFor(target)) +
  "\n" +
  buildTrackingIntervals(target.items)
    .map((interval) => interval.id)
    .join("\n");

interface ActiveWarm {
  src: string;
  signature: string;
  session: TrackingSession;
  stop: () => void;
  unsubscribe: () => void;
  intervals: ReadonlySet<string>;
}

export class BackgroundTrackingWarmer {
  private targets: readonly WarmTarget[] = [];
  private paused = true;
  private disposed = false;
  private active: ActiveWarm | null = null;
  private checking = false;
  private readonly slots: number;
  /** Videos taken as far as they go, against the intervals that got them there. */
  private finished = new Map<string, string>();

  constructor(private dependencies: WarmerDependencies = browserDependencies) {
    this.slots = dependencies.computeSlots();
    // Warming can start before any video has been displayed, so it cannot
    // assume a player has already sized the ceiling.
    configurePixelBudget(this.slots);
  }

  /** The videos worth warming, nearest first. Replaces any previous list. */
  update(targets: readonly WarmTarget[]): void {
    this.targets = targets;
    // Only a video whose intervals actually changed is worth revisiting.
    // Streaming rewrites annotations continuously, and forgetting everything
    // on each delta re-ran a finished video through a fresh session — a decode
    // worker and a tracking worker built and torn down to learn it was done.
    for (const target of targets) {
      if (target.src && this.finished.get(target.src) !== signature(target)) this.finished.delete(target.src);
    }
    if (this.active && !targets.some((target) => target.src === this.active?.src)) this.release();
    this.advance();
  }

  /** Paused while the asset on screen is still working. */
  setPaused(paused: boolean): void {
    if (this.paused === paused) return;
    this.paused = paused;
    // Stopping keeps every result already cached and costs at most the one
    // interval in flight, so yielding here gives up very little.
    if (paused) this.release();
    else this.advance();
  }

  dispose(): void {
    this.disposed = true;
    this.targets = [];
    this.release();
  }

  private release(): void {
    this.active?.unsubscribe();
    this.active?.stop();
    this.active = null;
  }

  private advance(): void {
    if (this.paused || this.disposed || this.active) return;
    // One slot is one interval's worth of the shared pixel ceiling, and the
    // warmer holding it leaves the player unable to admit anything at all.
    // The pause that should release it keys on the player having work in
    // flight, which is exactly what it cannot get — so it would never come.
    if (this.slots < 2) return;
    for (const target of this.targets) {
      if (!target.src || this.finished.has(target.src)) continue;
      const intervals = buildTrackingIntervals(target.items);
      if (!intervals.length) {
        this.finished.set(target.src, signature(target));
        continue;
      }
      this.run(target, intervals);
      return;
    }
  }

  private run(target: WarmTarget, intervals: readonly TrackingInterval[]): void {
    const session = this.dependencies.createSession({
      src: target.src,
      enabled: true,
      options: trackingOptionsFor(target),
      runKey: target.runKey,
    });
    // The same provider the player is given, so recovery spends the one shared
    // per-run budget and refines the frames that player would have refined.
    if (target.recoveryEnabled !== false && target.recoveryProvider) {
      session.configureRecovery({ enabled: true, provider: target.recoveryProvider });
    }
    session.update(intervals, 0);
    const unsubscribe = session.subscribe(() => this.check());
    const stop = session.start();
    this.active = {
      src: target.src,
      signature: signature(target),
      session,
      stop,
      unsubscribe,
      intervals: new Set(intervals.map((interval) => interval.id)),
    };
    // Everything may already have been cached by an earlier pass.
    this.check();
  }

  /** Move on once this video has nothing left that running longer would add. */
  private check(): void {
    if (this.checking) return;
    this.checking = true;
    // An interval is published as completed before it is offered to recovery,
    // so a check run inline would see finished tracking and no recovery yet,
    // and stop the session before recovery ever started.
    queueMicrotask(() => {
      this.checking = false;
      const active = this.active;
      if (!active || this.paused || this.disposed) return;
      const { activity, errors } = active.session.getSnapshot();
      // Nothing more will happen without workers, so move on rather than wait.
      if (activity.unavailableReason === undefined) {
        if (activity.active.size > 0) return;
        if (![...active.intervals].every((id) => activity.completed.has(id) || errors.has(id))) return;
        if ([...(activity.recovery?.intervals.values() ?? [])].some(isRecoveryPending)) return;
      }
      this.finished.set(active.src, active.signature);
      this.release();
      this.advance();
    });
  }
}
